import { BalanceData, BossMechanic } from "../data/balance";
import { LevelPhase, LevelSim } from "../sim/levelSim";

/**
 * Lifetime stats (FINISH_PLAN WP7, DESIGN §9.7): what the player has done across every battle — kills per enemy type
 * and elite affix, kills and damage per tower type, battles and wins — for the Almanac, tower mastery and the
 * achievements. Presentation-side bookkeeping: read from a finished battle, never fed back into a sim or a hash.
 */

/** Which kind of run a battle was (app Endgame.runKind): Heroic and Trial wins count on their own too. */
export type BattleKind = "plain" | "heroic" | "trial";

/** One finished battle, as the stats count it. */
export interface BattleFacts {
  readonly level: number;
  readonly won: boolean;
  /** Won with no leak. */
  readonly noLeak: boolean;
  readonly kind: BattleKind;
  /** Kills per enemy id. */
  readonly kills: Readonly<Record<string, number>>;
  /** Kills of enemies that fly. */
  readonly airKills: number;
  /** Elite kills per affix id. */
  readonly elites: Readonly<Record<string, number>>;
  /** Kills per tower id (the fortress is "base"), from the towers standing at the end. */
  readonly towerKills: Readonly<Record<string, number>>;
  /** Damage dealt per tower id. */
  readonly towerDamage: Readonly<Record<string, number>>;
  /** M36: the rare creatures (enemy ids) that appeared in the battle; absent = none (a caller from before rares). */
  readonly raresSeen?: readonly string[];
  /** M40: the Starfall Wyrm appeared in the battle / was slain in it; absent = no (a caller from before it). */
  readonly wyrmSeen?: boolean;
  readonly wyrmKilled?: boolean;
}

/** Battle counts in Daily Orders keys (meta/orderProgress.ts BattleTally): `kills:<enemy>`, `kills:air`, `kills:elite:<affix>`. */
type Counts = Readonly<Record<string, number>>;

const KILLS = "kills:", ELITE = "kills:elite:", AIR = "kills:air", ANY = "kills:any";

/**
 * A finished battle's facts from its sim (phase, leaks, the towers standing), the battle's BattleTally counts and the
 * host's damage per tower instance (app LevelSession.damageByTower). A tower sold during the battle takes its kills
 * and damage with it: the stats undercount a little rather than track every instance.
 */
export function battleFacts(sim: LevelSim, counts: Counts, damageByTower: ReadonlyMap<number, number>, kind: BattleKind = "plain", assistsByTower: ReadonlyMap<number, number> = new Map()): BattleFacts {
  const B = sim.balance;
  const kills: Record<string, number> = {}, elites: Record<string, number> = {}, towerKills: Record<string, number> = {}, towerDamage: Record<string, number> = {};
  for (const [key, n] of Object.entries(counts)) {
    if (!key.startsWith(KILLS) || key === AIR || key === ANY || !(n > 0)) continue;
    if (key.startsWith(ELITE)) elites[key.slice(ELITE.length)] = n;
    else kills[key.slice(KILLS.length)] = n;
  }
  for (const t of sim.towerSlots) {
    if (!t.alive || t.kills <= 0) continue;
    const id = B.towers[t.archetype].id;
    towerKills[id] = (towerKills[id] ?? 0) + t.kills;
  }
  // a Hex Totem deals no damage: its kills are the raiders that died under its curse (app LevelSession.assistsByTower)
  for (const [towerId, n] of assistsByTower) {
    const t = sim.findTower(towerId);
    if (!t || !(n > 0)) continue;
    const id = B.towers[t.archetype].id;
    towerKills[id] = (towerKills[id] ?? 0) + n;
  }
  for (const [towerId, dmg] of damageByTower) {
    const t = sim.findTower(towerId);
    if (!t || !(dmg > 0)) continue;
    const id = B.towers[t.archetype].id;
    towerDamage[id] = (towerDamage[id] ?? 0) + Math.round(dmg);
  }
  const won = sim.phase === LevelPhase.Won;
  const raresSeen = [...new Set(sim.rareRuns.map((r) => B.enemies[r.archetype].id))];   // M37: Endless may bring several
  const wyrmSeen = sim.wyrmRun !== null, wyrmKilled = sim.wyrmRun?.outcome === "killed";   // M40 (sim/wyrm.ts)
  return { level: sim.levelNumber, won, noLeak: won && sim.leaks === 0, kind, kills, airKills: counts[AIR] ?? 0, elites, towerKills, towerDamage, raresSeen, wyrmSeen, wyrmKilled };
}

export interface StatsJson {
  v: number;
  battles: number; wins: number; noLeakWins: number; heroicWins: number; trialWins: number;
  airKills: number;
  kills: Record<string, number>; elites: Record<string, number>; towerKills: Record<string, number>; towerDamage: Record<string, number>;
  /** The newest recorded battles' keys: a battle is counted once however often its end is reported. */
  runs: string[];
  /** M36: battles in which each rare creature (enemy id) appeared (the Almanac's page opens on the first). */
  raresSeen?: Record<string, number>;
  /** M36: keys of the rare chests already banked (newest last), so a resumed or retried run never banks one twice. */
  rareChests?: string[];
  /** M40: battles the Starfall Wyrm appeared in / was slain in. */
  wyrmsSeen?: number;
  wyrmsKilled?: number;
  /** M40: the Wyrm rewards already granted, "key=skin id" or "key=chest" (newest last): granted once a battle. */
  wyrmGrants?: string[];
  /** M40: the Wyrm retries already paid for, by key (newest last): a replayed decision never charges twice. */
  wyrmRetries?: string[];
}

export const STATS_VERSION = 1;
/** Recorded battle keys kept (a report arrives right after its battle, so a handful is plenty). */
const RUNS_KEPT = 8;
/** Banked rare chest keys kept: one a battle at most, and a retry rolls the same key, so a short list is plenty. */
const RARE_CHESTS_KEPT = 32;
/** Wyrm grant and retry keys kept (M40): one Wyrm a battle at most, a few retries each. */
const WYRM_KEYS_KEPT = 32;
const WYRM_KEY_LENGTH = 96;
/** An id we accept as a stats key (enemy, affix and tower ids are short lower-camel words). */
const KEY = /^[A-Za-z0-9_]{1,48}$/;

const isCount = (n: unknown): n is number => Number.isSafeInteger(n) && (n as number) >= 0;

function addInto(into: Record<string, number>, from: Readonly<Record<string, number>>): void {
  for (const [k, n] of Object.entries(from)) if (KEY.test(k) && isCount(n) && n > 0) into[k] = (into[k] ?? 0) + n;
}
function countMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof v !== "object" || v === null || Array.isArray(v)) return out;
  addInto(out, v as Record<string, number>);
  return out;
}
const sum = (m: Readonly<Record<string, number>>): number => Object.values(m).reduce((a, n) => a + n, 0);
/** Appends a key to a kept list (M40 Wyrm keys), dropping the oldest past WYRM_KEYS_KEPT. */
function keep(list: string[], key: string): void {
  list.push(key);
  if (list.length > WYRM_KEYS_KEPT) list.splice(0, list.length - WYRM_KEYS_KEPT);
}

export class LifetimeStats {
  battles = 0;
  wins = 0;
  noLeakWins = 0;
  heroicWins = 0;
  trialWins = 0;
  airKills = 0;
  readonly kills: Record<string, number> = {};
  readonly elites: Record<string, number> = {};
  readonly towerKills: Record<string, number> = {};
  readonly towerDamage: Record<string, number> = {};
  /** M36: battles each rare creature appeared in, by enemy id. */
  readonly raresSeen: Record<string, number> = {};
  private readonly runs: string[] = [];
  private readonly rareChests: string[] = [];
  /** M40: battles the Starfall Wyrm appeared in / was slain in. */
  wyrmsSeen = 0;
  wyrmsKilled = 0;
  private readonly wyrmGrants: string[] = [];
  private readonly wyrmRetries: string[] = [];

  /** Adds a battle once: false (changing nothing) when `key` was already recorded. */
  record(f: BattleFacts, key: string): boolean {
    if (this.runs.includes(key)) return false;
    this.runs.push(key);
    if (this.runs.length > RUNS_KEPT) this.runs.splice(0, this.runs.length - RUNS_KEPT);
    this.battles++;
    if (f.won) {
      this.wins++;
      if (f.noLeak) this.noLeakWins++;
      if (f.kind === "heroic") this.heroicWins++;
      if (f.kind === "trial") this.trialWins++;
    }
    if (isCount(f.airKills)) this.airKills += f.airKills;
    addInto(this.kills, f.kills);
    addInto(this.elites, f.elites);
    addInto(this.towerKills, f.towerKills);
    addInto(this.towerDamage, f.towerDamage);
    for (const id of f.raresSeen ?? []) if (KEY.test(id)) this.raresSeen[id] = (this.raresSeen[id] ?? 0) + 1;
    if (f.wyrmSeen === true) this.wyrmsSeen++;
    if (f.wyrmKilled === true) this.wyrmsKilled++;
    return true;
  }

  /**
   * M36: a rare chest is about to be banked under `key` (its seed): true the first time (bank it), false when that
   * chest was banked already (a resumed battle replays the kill; a checkpoint retry kills the same rare for the same chest).
   */
  noteRareChest(key: string): boolean {
    if (this.rareChests.includes(key)) return false;
    this.rareChests.push(key);
    if (this.rareChests.length > RARE_CHESTS_KEPT) this.rareChests.splice(0, this.rareChests.length - RARE_CHESTS_KEPT);
    return true;
  }

  /** M40: the Wyrm reward granted under `key` ("skin id" or "chest"), or null when none was yet. */
  wyrmGrantOf(key: string): string | null {
    const hit = this.wyrmGrants.find((g) => g.startsWith(`${key}=`));
    return hit === undefined ? null : hit.slice(key.length + 1);
  }
  /** M40: a Wyrm reward is granted under `key`: true the first time, false when it was granted already (a resumed battle). */
  noteWyrmGrant(key: string, what: string): boolean {
    if (this.wyrmGrantOf(key) !== null) return false;
    keep(this.wyrmGrants, `${key}=${what}`);
    return true;
  }
  /** M40: whether the Wyrm retry under `key` (seed, stage, attempt) was paid for already (a replayed decision is free). */
  wyrmRetryPaid(key: string): boolean { return this.wyrmRetries.includes(key); }
  /** M40: the Wyrm retry under `key` is paid. */
  noteWyrmRetry(key: string): void { if (!this.wyrmRetryPaid(key)) keep(this.wyrmRetries, key); }

  /** M36: whether a rare creature (enemy id) has ever appeared to the player (its Almanac page is open). */
  hasSeenRare(id: string): boolean { return (this.raresSeen[id] ?? 0) > 0; }
  /** M36: rare creatures seen at least once / killed at least once. */
  raresSeenCount(balance: BalanceData): number { return balance.enemies.filter((e) => e.rareChapter > 0 && this.hasSeenRare(e.id)).length; }
  raresKilled(balance: BalanceData): number { return balance.enemies.filter((e) => e.rareChapter > 0 && this.killsOf(e.id) > 0).length; }

  get totalKills(): number { return sum(this.kills); }
  get eliteKills(): number { return sum(this.elites); }
  killsOf(enemyId: string): number { return this.kills[enemyId] ?? 0; }
  killsBy(towerId: string): number { return this.towerKills[towerId] ?? 0; }
  damageBy(towerId: string): number { return this.towerDamage[towerId] ?? 0; }

  /** Boss types defeated at least once. */
  bossesDefeated(balance: BalanceData): number {
    return balance.enemies.filter((e) => e.bossMechanic !== BossMechanic.None && this.killsOf(e.id) > 0).length;
  }

  toJson(): StatsJson {
    return {
      v: STATS_VERSION, battles: this.battles, wins: this.wins, noLeakWins: this.noLeakWins, heroicWins: this.heroicWins, trialWins: this.trialWins, airKills: this.airKills,
      kills: { ...this.kills }, elites: { ...this.elites }, towerKills: { ...this.towerKills }, towerDamage: { ...this.towerDamage }, runs: [...this.runs],
      raresSeen: { ...this.raresSeen }, rareChests: [...this.rareChests],
      wyrmsSeen: this.wyrmsSeen, wyrmsKilled: this.wyrmsKilled, wyrmGrants: [...this.wyrmGrants], wyrmRetries: [...this.wyrmRetries],
    };
  }

  /** Restores the stats field by field: a malformed count reads 0, a malformed map entry is dropped. */
  static fromJson(j: unknown): LifetimeStats {
    const s = new LifetimeStats();
    if (typeof j !== "object" || j === null || Array.isArray(j)) return s;
    const src = j as Partial<StatsJson>;
    for (const k of ["battles", "wins", "noLeakWins", "heroicWins", "trialWins", "airKills", "wyrmsSeen", "wyrmsKilled"] as const) if (isCount(src[k])) s[k] = src[k] as number;
    addInto(s.kills, countMap(src.kills));
    addInto(s.elites, countMap(src.elites));
    addInto(s.towerKills, countMap(src.towerKills));
    addInto(s.towerDamage, countMap(src.towerDamage));
    if (Array.isArray(src.runs)) for (const r of src.runs.slice(-RUNS_KEPT)) if (typeof r === "string" && r.length <= 128) s.runs.push(r);
    addInto(s.raresSeen, countMap(src.raresSeen));
    if (Array.isArray(src.rareChests)) for (const r of src.rareChests.slice(-RARE_CHESTS_KEPT)) if (typeof r === "string" && r.length <= 64) s.rareChests.push(r);
    for (const [from, into] of [[src.wyrmGrants, s.wyrmGrants], [src.wyrmRetries, s.wyrmRetries]] as const) {
      if (Array.isArray(from)) for (const r of from.slice(-WYRM_KEYS_KEPT)) if (typeof r === "string" && r.length <= WYRM_KEY_LENGTH) into.push(r);
    }
    return s;
  }
}

/** The mastery tier a tower type has reached from its lifetime kills: an index into `tiers`, −1 for none yet. */
export function masteryTier(tiers: readonly { readonly kills: number }[], kills: number): number {
  let t = -1;
  tiers.forEach((tier, i) => { if (kills >= tier.kills) t = i; });
  return t;
}
